/**
 * Quick View Modal for Kavora Fashion
 * Allows users to view product details without leaving the collection page
 */

class QuickViewModal extends HTMLElement {
  constructor() {
    super();
    this.isOpen = false;
    this.productHandle = null;
  }

  connectedCallback() {
    this.render();
    this.bindEvents();
  }

  render() {
    this.innerHTML = `
      <div class="quick-view-modal">
        <div class="quick-view-modal__overlay"></div>
        <div class="quick-view-modal__content">
          <button type="button" class="quick-view-modal__close" aria-label="Close">
            <svg width="24" height="24" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
              <path d="M18 6L6 18M6 6l12 12"/>
            </svg>
          </button>
          <div class="quick-view-modal__body">
            <div class="quick-view-loading">
              <div class="spinner"></div>
              <p>Loading product...</p>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  bindEvents() {
    this.querySelector('.quick-view-modal__close').addEventListener('click', () => this.close());
    this.querySelector('.quick-view-modal__overlay').addEventListener('click', () => this.close());
    
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen) {
        this.close();
      }
    });
  }

  async open() {
    this.productHandle = this.getAttribute('handle');
    
    this.isOpen = true;
    this.classList.add('is-open');
    document.body.style.overflow = 'hidden';
    
    await this.loadProduct();
  }

  close() {
    this.isOpen = false;
    this.classList.remove('is-open');
    document.body.style.overflow = '';
  }

  async loadProduct() {
    const body = this.querySelector('.quick-view-modal__body');
    
    try {
      const response = await fetch(`/products/${this.productHandle}?view=quick-view`);
      
      if (!response.ok) {
        const fallbackResponse = await fetch(`/products/${this.productHandle}.js`);
        const product = await fallbackResponse.json();
        this.renderProductFromJSON(product);
      } else {
        const html = await response.text();
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const quickViewContent = doc.querySelector('.quick-view-content');
        
        if (quickViewContent) {
          body.innerHTML = quickViewContent.innerHTML;
        } else {
          const fallbackResponse = await fetch(`/products/${this.productHandle}.js`);
          const product = await fallbackResponse.json();
          this.renderProductFromJSON(product);
        }
      }
    } catch (error) {
      console.error('Failed to load product:', error);
      body.innerHTML = '<p class="quick-view-error">Failed to load product. Please try again.</p>';
    }
  }

  renderProductFromJSON(product) {
    const body = this.querySelector('.quick-view-modal__body');
    
    const firstImage = product.featured_image || product.images[0];
    const price = this.formatMoney(product.price);
    const compareAtPrice = product.compare_at_price ? this.formatMoney(product.compare_at_price) : null;
    
    body.innerHTML = `
      <div class="quick-view-product">
        <div class="quick-view-product__gallery">
          <img src="${firstImage}" alt="${product.title}" class="quick-view-product__image">
          ${product.images.length > 1 ? `
            <div class="quick-view-product__thumbs">
              ${product.images.slice(0, 4).map(img => `
                <button type="button" class="quick-view-thumb">
                  <img src="${img}" alt="${product.title}">
                </button>
              `).join('')}
            </div>
          ` : ''}
        </div>
        
        <div class="quick-view-product__details">
          ${product.vendor ? `<p class="quick-view-product__vendor">${product.vendor}</p>` : ''}
          <h2 class="quick-view-product__title">${product.title}</h2>
          
          <div class="quick-view-product__price">
            ${compareAtPrice ? `
              <span class="quick-view-product__sale-price">${price}</span>
              <span class="quick-view-product__compare-price">${compareAtPrice}</span>
            ` : `
              <span class="quick-view-product__regular-price">${price}</span>
            `}
          </div>
          
          ${product.description ? `
            <div class="quick-view-product__description">
              ${product.description.substring(0, 200)}${product.description.length > 200 ? '...' : ''}
            </div>
          ` : ''}
          
          ${product.variants.length > 1 ? this.renderVariantSelector(product) : ''}
          
          <form method="post" action="/cart/add">
            <input type="hidden" name="id" value="${product.variants[0].id}">
            <button type="submit" class="quick-view-product__add-to-cart ${!product.available ? 'disabled' : ''}">
              ${product.available ? 'Add to Cart' : 'Sold Out'}
            </button>
          </form>
          
          <a href="${product.url}" class="quick-view-product__full-details">View Full Details →</a>
        </div>
      </div>
    `;
    
    this.bindProductEvents();
  }

  renderVariantSelector(product) {
    if (!product.options || product.options.length === 0) return '';
    
    return `
      <div class="quick-view-product__options">
        ${product.options.map((option, index) => `
          <div class="quick-view-option">
            <label>${option.name}</label>
            <select class="quick-view-option__select" data-option-index="${index}">
              ${option.values.map(value => `
                <option value="${value}">${value}</option>
              `).join('')}
            </select>
          </div>
        `).join('')}
      </div>
    `;
  }

  bindProductEvents() {
    const thumbs = this.querySelectorAll('.quick-view-thumb');
    const mainImage = this.querySelector('.quick-view-product__image');
    
    thumbs.forEach(thumb => {
      thumb.addEventListener('click', (e) => {
        const img = e.currentTarget.querySelector('img');
        if (mainImage && img) {
          mainImage.src = img.src;
        }
      });
    });
  }

  formatMoney(cents) {
    const amount = (cents / 100).toFixed(2);
    return `£${amount} GBP`;
  }
}

if (!window.customElements.get('quick-view-modal')) {
  window.customElements.define('quick-view-modal', QuickViewModal);
}

window.openQuickView = function(handle) {
  let modal = document.querySelector(`quick-view-modal[handle="${handle}"]`);
  
  if (!modal) {
    modal = document.createElement('quick-view-modal');
    modal.setAttribute('handle', handle);
    document.body.appendChild(modal);
    
    setTimeout(() => modal.open(), 100);
  } else {
    modal.open();
  }
};
